import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Image from '../../components/AppImage';
import { officialService } from '../../services/officialService';
import { tournamentService } from '../../services/tournamentService';

const TournamentOfficials = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [leagueOfficials, setLeagueOfficials] = useState([]);
  const [selectedOfficials, setSelectedOfficials] = useState([]);
  const [selectedOfficialId, setSelectedOfficialId] = useState('');
  const [assignedRole, setAssignedRole] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/admin-dashboard' },
    { label: 'Tournament Management' },
    { label: 'Tournament Officials' },
  ];

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [allOfficials, tournamentOfficials] = await Promise.all([
        officialService.getAll(),
        tournamentService.getSelectedOfficials(),
      ]);
      setLeagueOfficials(allOfficials || []);
      setSelectedOfficials(tournamentOfficials || []);
    } catch (err) {
      setError(err.message || 'Failed to load officials.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const selectedIds = useMemo(
    () => new Set(selectedOfficials.map((item) => item.officialId)),
    [selectedOfficials]
  );

  const availableOptions = useMemo(
    () =>
      leagueOfficials
        .filter((official) => !selectedIds.has(official.id))
        .map((official) => ({
          value: official.id,
          label: `${official.userProfile?.fullName || 'Official'} (${official.specialization || 'N/A'})`,
        })),
    [leagueOfficials, selectedIds]
  );

  const roleOptions = [
    { value: 'Referee', label: 'Referee' },
    { value: 'Table Official', label: 'Table Official' },
    { value: 'Commissioner', label: 'Commissioner' },
  ];

  const handleSelect = async () => {
    setError(null);
    setSuccess(null);
    if (!selectedOfficialId) {
      setError('Select an official from the league officials list.');
      return;
    }
    setIsSaving(true);
    try {
      await tournamentService.selectOfficial(selectedOfficialId, assignedRole || null);
      setSuccess('Official added to tournament selection.');
      setSelectedOfficialId('');
      setAssignedRole('');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to select official.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async (selectionId) => {
    try {
      await tournamentService.removeSelectedOfficial(selectionId);
      setSelectedOfficials((prev) => prev.filter((item) => item.id !== selectionId));
      setSuccess('Official removed from tournament selection.');
    } catch (err) {
      setError(err.message || 'Failed to remove official.');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="p-6">
          <Breadcrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between mt-4 mb-6 gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="UserCheck" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Tournament Selected Officials</h1>
                <p className="text-muted-foreground">
                  Choose tournament officials from the registered league officials list
                </p>
              </div>
            </div>
            <Button variant="outline" iconName="ArrowLeft" iconPosition="left" onClick={() => navigate('/admin-dashboard')}>
              Back to Dashboard
            </Button>
          </div>

          {success && (
            <div className="mb-4 px-4 py-3 rounded-lg border bg-success/10 border-success/20 text-success">{success}</div>
          )}
          {error && (
            <div className="mb-4 px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive">{error}</div>
          )}

          <div className="bg-card rounded-lg border border-border card-shadow p-6 mb-6 max-w-4xl space-y-4">
            <h2 className="text-lg font-semibold text-foreground">Add Official</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="League Official"
                options={availableOptions}
                value={selectedOfficialId}
                onChange={setSelectedOfficialId}
                placeholder={isLoading ? 'Loading officials...' : 'Select from league officials'}
                searchable
                disabled={isLoading || availableOptions.length === 0}
              />
              <Select
                label="Assigned Tournament Role"
                options={roleOptions}
                value={assignedRole}
                onChange={setAssignedRole}
                placeholder="Optional role"
              />
            </div>
            <Button
              type="button"
              iconName="UserPlus"
              iconPosition="left"
              loading={isSaving}
              onClick={handleSelect}
              disabled={!selectedOfficialId}
            >
              Add to Tournament
            </Button>
          </div>

          <div className="bg-card rounded-lg border border-border card-shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">Selected Tournament Officials</h2>
              <span className="text-sm text-muted-foreground">{selectedOfficials.length} selected</span>
            </div>
            {isLoading ? (
              <div className="p-8 flex items-center justify-center gap-3 text-muted-foreground">
                <Icon name="Loader2" size={20} className="animate-spin" />
                Loading...
              </div>
            ) : selectedOfficials.length === 0 ? (
              <p className="p-6 text-sm text-muted-foreground">
                No tournament officials selected yet. Pick from the league officials list above.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-6">
                {selectedOfficials.map((item) => (
                  <div key={item.id} className="border border-border rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden bg-muted">
                        <Image
                          src={item.official?.avatarUrl || '/assets/images/no_image.png'}
                          alt={item.official?.fullName || 'Official'}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{item.official?.fullName}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.assignedRole || item.official?.specialization || 'Official'}
                        </p>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>{item.official?.email || 'No email'}</p>
                      <p>{item.official?.phone || 'No phone'}</p>
                      <p>License: {item.official?.licenseNumber || '—'}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => handleRemove(item.id)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default TournamentOfficials;
